import {
  StoryblokComponent,
  useStoryblokState,
  type ISbStoryData
} from '@storyblok/react'
import type { GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { BusinessFinder } from '~/components/BusinessFinder/BusinessFinder'
import { mapBussinessFinder } from '~/components/BusinessFinder/BusinessFinder.mapping'
import { type BusinessSliderProps } from '~/components/BusinessSlider/BusinessSlider.interface'
import { mapBussinessSlider } from '~/components/BusinessSlider/BusinessSlider.mapping'
import { Footer } from '~/components/Footer/Footer'
import { createPreloadImage } from '~/components/Image/utils'

import { Layout } from '~/components/Layout/Layout'
import { mapLayout } from '~/components/Layout/Layout.mapping'
import { PageMetadata } from '~/components/PageMetadata/PageMetadata'
import { type PageMetadataProps } from '~/components/PageMetadata/PageMetadata.interface'
import { mapPageMetadata } from '~/components/PageMetadata/PageMetadata.mapping'
import { shuffleArray } from '~/lib'
import { fetchBusinessesContent, fetchAllStoryblokContent } from '~/lib/storyblok'
import { type ISbStory } from '~/types'
import { type GlobalSettingsStoryblok, type HomeStoryblok } from '~/types/generated'

export interface HomeProps extends PageMetadataProps {
  story: ISbStoryData<HomeStoryblok>;
  settings: ISbStory;
  layout: ISbStory;
  businesses: BusinessSliderProps;
  finder: ISbStory;
}

const BusinessSliderClient = dynamic(
  async () => await import('~/components/BusinessSlider/BusinessSlider').then((mod) => mod.BusinessSlider),
  { ssr: false }
)

const Home: NextPage<HomeProps> = (ctx): JSX.Element => {
  const { metadata, story: initialStory, layout, businesses, finder } = ctx
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const story = useStoryblokState(initialStory)
  const hero = (story as ISbStory)?.content?.hero?.[0]

  // eslint-disable-next-line max-len, @typescript-eslint/no-explicit-any
  const uniqueBusinessFinder = finder.businessTypes.filter((v: { value: any }, i: any, a: any[]) => a.findIndex((t: { value: any }) => (t.value === v.value)) === i)
  const searchLabel = (story as ISbStory)?.content.searchLabel
  const searchButton = finder?.searchButton

  const businessImages = businesses.items?.map((business) => business.image).slice(0, 3)
  const cardSizes = '100vw, (min-width: 1328px) 50vw'
  const preloadImageData = businessImages
    ?.map((image) => createPreloadImage(
      { src: image.src, width: image.width, height: image.height, alt: image.alt, sizes: cardSizes }
    ))

  return (
    <>
      <Head>
        {
          preloadImageData?.map((image) => (
            <link rel="preload" as="image" href={image.src} key={image.src} imageSrcSet={image.imagesrcset} imageSizes={image.imagesizes} />
          ))
        }
      </Head>
      <PageMetadata metadata={metadata} />
      <Layout layout={layout} businessFinder={finder}>
        <main>
          <div className="page-grid">
            <div className="page-grid-content page-grid-content-sm">
              <div className="page-grid-content-grid">
                <div>
                  {hero && <StoryblokComponent blok={hero} />}
                  {story && <BusinessFinder typeTitle={searchLabel} businessTypes={uniqueBusinessFinder} categoryTitle="Choose a category or three (if you like)..." searchButton={searchButton} />}
                </div>
              </div>
            </div>
            <div className="page-grid-slider">
              <BusinessSliderClient items={businesses.items} cardSizes={cardSizes} />
            </div>
            <div className="page-grid-footer">
              {layout.footer && (
                <Footer
                  copyrightText={layout.footer.copyrightText}
                  footerLinks={layout.footer.footerLinks}
                  siteByLabel={layout.footer.siteByLabel}
                  siteByLink={layout.footer.siteByLink}
                  noContainer
                />
              )}
            </div>
          </div>
        </main>
      </Layout>
    </>
  )
}

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = 'home'

  const version = context.draftMode ? 'draft' : 'published'

  const getStoryblokStories = await fetchAllStoryblokContent(
    {
      by_slugs: `global-settings,${slug}`,
      resolve_relations: 'globalSettings.footerNavigation',
      version
    }
  )

  const globalSettingsData = getStoryblokStories.find((content: { slug: string }) => content.slug === 'global-settings')
  const pageData = getStoryblokStories.find((content: { slug: string }) => content.slug === slug)

  if (!globalSettingsData || !pageData) {
    return { notFound: true }
  }

  const businessesData = await fetchBusinessesContent({
    version: 'published'
  })

  if (!businessesData) {
    return { notFound: true }
  }

  const metadata = mapPageMetadata(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    pageData.content.seoMetadata[0],
    '',
    globalSettingsData.content as GlobalSettingsStoryblok
  )

  const layout = mapLayout(globalSettingsData.content as GlobalSettingsStoryblok, true)

  const businessCategories = businessesData
    .map((bussiness: ISbStory) => bussiness.content.businessCategory[0])
  const finder = mapBussinessFinder(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    businessCategories,
    globalSettingsData.content as GlobalSettingsStoryblok
  )

  // Filter out any business that have not been approved to show on the business slider from the cms
  const homePageSliderBusinesses = businessesData.filter((business: BusinessSliderProps) => {
    const hasApproval = business.content.businessImages?.[0]?.addToHomepageSlider
    return hasApproval
  })

  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const shuffleBussiness = shuffleArray([...homePageSliderBusinesses]).slice(0, 15)
  const bussinessesArray = mapBussinessSlider(shuffleBussiness)

  return {
    props: {
      metadata,
      story: pageData ?? false,
      finder,
      businesses: bussinessesArray ?? false,
      layout
    },
    revalidate: 3600 // revalidate every hour
  }
}

export default Home
